<template>
  <NuxtLink
    v-if="to"
    :to="to"
    v-bind="$attrs"
    :class="btnVariant"
    :disabled="disabled"
    :target="target ? target : useIsExternalUrl(to) ? '_blank' : '_self'"
  >
    <slot />
    <Component
      :is="iconType.icon"
      v-if="iconType.icon !== 'none'"
      class="ml-[0.35em] mt-[0.1em] w-[1.55em] shrink-0 transition-transform ease-in-out"
      :class="iconType.hoverStyle"
    />
  </NuxtLink>
  <button
    v-else
    :class="btnVariant"
    v-bind="$attrs"
    :disabled="disabled"
    type="button"
  >
    <slot />
    <Component
      :is="iconType.icon"
      v-if="iconType.icon !== 'none'"
      class="ml-[0.275em] w-[1.55em] shrink-0 transition-transform ease-in-out"
      :class="iconType.hoverStyle"
    />

    <!-- SPINNER ICON -->
    <div
      class="transition-[width]"
      :class="isLoading ? 'w-[2em]' : 'w-0'"
    >
      <Transition name="fade">
        <div v-if="isLoading">
          <IconSpin class="ml-[0.75em] size-[1.25em] animate-spin" />
        </div>
      </Transition>
    </div>
  </button>
</template>

<script setup lang="ts">
const props = defineProps({
  to: {
    type: [String, Object],
    default: null,
  },

  target: {
    type: String,
    default: null,
  },

  /*
    none: no icon
    default: Arrow for links, plus for buttons
    invert: Arrow for buttons, plus for links
    download: download icon
    {customIconName}: custom icon
  */
  icon: {
    type: String as PropType<'none' | 'default' | 'invert' | 'download' | string>,
    default: 'default',
  },

  // Overrides the hover style of the icon
  iconHoverStyle: {
    type: String,
    default: '',
  },

  variant: {
    type: String as PropType<'primary' | 'outlined' | 'outlined-dark'>,
    default: 'primary',
  },

  size: {
    type: String as PropType<'sm' | 'md' >,
    default: 'md',
  },

  disabled: {
    type: Boolean,
    default: false,
  },

  isLoading: {
    type: Boolean,
    default: false,
  },
})

const btnVariant = computed(() => {
  // Default classes
  const classes = [`group flex h-fit w-fit items-center rounded-full px-6 py-3 transition duration-200`]

  // Add size classes
  if (props.size === 'sm') {
    classes.push('typo-button-text-sm')
  } else if (props.size === 'md') {
    classes.push('typo-button-text-xl')
  }

  // Add variant classes
  if (props.variant === 'primary') {
    classes.push('bg-blue-500 border-1.5 border-blue-500')
    if (props.disabled) {
      classes.push('text-white/30 cursor-not-allowed')
    } else {
      classes.push('text-white hover:bg-blue-600 group-hover:bg-blue-600 hover:border-blue-600 group-hover:border-blue-600')
    }
  }

  if (props.variant === 'outlined') {
    classes.push('border-1.5 border-current bg-transparent')
    if (props.disabled) {
      classes.push('text-white/30 cursor-not-allowed')
    } else {
      classes.push('text-current hover:bg-white/10 group-hover:bg-white/10')
    }
  }

  if (props.variant === 'outlined-dark') {
    classes.push('border-1.5 border-dark bg-transparent')
    if (props.disabled) {
      classes.push('text-dark/30 cursor-not-allowed')
    } else {
      classes.push('hover:bg-theme-300 group-hover:bg-theme-300')
    }
  }

  return classes
})

const iconType = computed(() => {
  let iconName
  let hoverStyle = ''

  switch (props.icon) {
    case 'none':
      iconName = 'none'
      break
    case 'default':
      if (props.to) {
        iconName = 'IconArrowLink'
        hoverStyle = 'group-hover:-translate-y-1 group-hover:translate-x-1'
      } else {
        iconName = 'IconPlus'
        hoverStyle = 'group-hover:rotate-90'
      }
      break
    case 'invert':
      if (!props.to) {
        iconName = 'IconArrowLink'
        hoverStyle = 'group-hover:-translate-y-1 group-hover:translate-x-1'
      } else {
        iconName = 'IconPlus'
        hoverStyle = 'group-hover:rotate-90'
      }
      break
    case 'download':
      iconName = 'IconDownload'
      hoverStyle = ''
      break
    default:
      iconName = props.icon
  }

  // Override hover style if provided
  if (props.iconHoverStyle) {
    hoverStyle = props.iconHoverStyle
  }

  return {
    icon: iconName,
    hoverStyle,
  }
})
</script>
